import { Link } from 'react-router-dom';

const LAST_UPDATED = 'May 15, 2026';

export default function Terms() {
  return (
    <div className="legal">
      <h1>Terms of Service</h1>
      <p className="muted">Last updated: {LAST_UPDATED}</p>

      <p>
        These Terms of Service (&ldquo;Terms&rdquo;) govern your use of
        Invitey, a multi-tenant calendar booking service operated by{' '}
        <strong>Get Proof Digital Inc.</strong>, a Saskatchewan corporation
        doing business as &ldquo;Proof Digital&rdquo; and, for the service
        described here, as &ldquo;Invitey&rdquo; (collectively
        &ldquo;Invitey&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). By
        accessing or using Invitey &mdash; whether through{' '}
        <code>invitey.ca</code>, an embedded widget, or a custom hostname
        routed to us &mdash; you agree to these Terms.
      </p>

      <h2>1. Accounts</h2>
      <p>
        To act as an Organization admin or staff member, you create an account
        through our authentication provider, Clerk. You are responsible for
        keeping your credentials secure and for all activity under your
        account. You must be at least the local age of digital consent (13 in
        most jurisdictions) to use Invitey.
      </p>

      <h2>2. The service</h2>
      <p>Invitey lets an Organization:</p>
      <ul>
        <li>Define locations, services, staff, and availability rules.</li>
        <li>
          Publish a hosted booking page at <code>invitey.ca/&lt;org-slug&gt;</code>,
          an embeddable widget, or a custom hostname.
        </li>
        <li>
          Sync bookings with each staff member&rsquo;s connected Google or
          Outlook calendar.
        </li>
        <li>
          Send confirmation, reminder, reschedule, and cancellation notices to
          bookees over email and (where opted in) SMS.
        </li>
        <li>
          Receive outbound webhooks for booking events at an endpoint of the
          Organization&rsquo;s choosing.
        </li>
      </ul>

      <h2>3. Acceptable use</h2>
      <p>You agree not to:</p>
      <ul>
        <li>
          Use Invitey to send spam, unsolicited marketing, or any
          communication that violates anti-spam laws (CASL, CAN-SPAM, TCPA, or
          local equivalents).
        </li>
        <li>
          Use Invitey to collect personal data for purposes the bookee did
          not consent to, or in violation of applicable data-protection laws.
        </li>
        <li>
          Attempt to disrupt, reverse-engineer, scrape, or overwhelm the
          service, including by issuing automated requests that exceed any
          published or communicated rate limits.
        </li>
        <li>
          Use Invitey to facilitate unlawful activity, harassment, or
          discrimination.
        </li>
        <li>
          Resell or sublicense Invitey to a third party except through the
          Organization-and-staff structure the product is designed for.
        </li>
      </ul>

      <h2>4. The Organization&rsquo;s data and the bookee&rsquo;s data</h2>
      <p>
        Bookees (the customers your Organization takes appointments from) have
        their own privacy interests. When you use Invitey, you confirm that
        you have a lawful basis to collect their information through the
        booking flow and that you will handle it consistent with our{' '}
        <Link to="/privacy">Privacy Policy</Link> and applicable law.
      </p>
      <p>
        You retain ownership of all data you and your bookees enter into
        Invitey. You grant us a limited licence to host, process, transmit,
        and display that data only as needed to provide the service.
      </p>

      <h2>5. Third-party services</h2>
      <p>
        Invitey integrates with third-party services including Google,
        Microsoft, Clerk, Cloudflare, Mailgun, and Twilio. Your use of those
        services through Invitey is also subject to their own terms. Invitey
        is not responsible for outages, data loss, or policy changes by these
        third parties.
      </p>

      <h2>6. Fees</h2>
      <p>
        Pricing, billing cycles, and payment terms (if any) are communicated
        to the Organization separately. The hosted booking experience and
        widget are provided to bookees free of charge by Invitey.
      </p>

      <h2>7. Suspension and termination</h2>
      <p>
        We may suspend or terminate access to Invitey if you materially breach
        these Terms, if continued service would expose us or our users to
        legal or security risk, or if we are required to do so by law. You may
        terminate your account at any time by deleting your Organization
        through the admin dashboard or by contacting support.
      </p>
      <p>
        On termination we will delete or anonymize your data within 30 days,
        except where retention is required by law. You can export your
        bookings and configuration via our API before deletion.
      </p>

      <h2>8. Service availability and changes</h2>
      <p>
        Invitey is provided on an as-available basis. We work to keep the
        service reliable, but we do not guarantee uninterrupted availability,
        and we may change, add, or remove features over time. Where a change
        materially reduces functionality you rely on, we will give reasonable
        notice to Organization admins.
      </p>

      <h2>9. Disclaimer of warranties</h2>
      <p>
        Invitey is provided &ldquo;as is&rdquo; and &ldquo;as
        available&rdquo;, without warranties of any kind, whether express,
        implied, or statutory, including warranties of merchantability,
        fitness for a particular purpose, and non-infringement, to the maximum
        extent permitted by law.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law, Get Proof Digital Inc. (and
        its affiliates, officers, employees, and agents) will not be liable
        for any indirect, incidental, special, consequential, or exemplary
        damages, or for lost profits, revenues, data, or goodwill, arising
        out of or related to your use of the service. Our total aggregate
        liability for any claim arising out of or related to these Terms or
        the service is limited to the greater of (a) the amount you paid us
        in the 12 months preceding the event giving rise to the claim, or
        (b) CAD $100.
      </p>

      <h2>11. Indemnity</h2>
      <p>
        You agree to indemnify and hold Get Proof Digital Inc. (and its
        affiliates, officers, employees, and agents) harmless from any
        claims, damages, liabilities, and costs (including reasonable legal
        fees) arising from your use of the service in violation of these
        Terms or applicable law, or from data you or your bookees submit to
        Invitey.
      </p>

      <h2>12. Governing law</h2>
      <p>
        These Terms are governed by the laws of the Province of Saskatchewan
        and the federal laws of Canada applicable therein, without regard to
        conflict-of-laws principles. The courts located in Saskatchewan have
        exclusive jurisdiction over disputes arising out of or related to
        these Terms, except that either party may seek injunctive relief in
        any competent court to protect intellectual property or
        confidentiality.
      </p>

      <h2>13. Changes to these Terms</h2>
      <p>
        We will update the &ldquo;Last updated&rdquo; date when these Terms
        change. Continued use of Invitey after a change constitutes acceptance
        of the new Terms. Material changes will be communicated to
        Organization admins by email.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions about these Terms can be sent to{' '}
        <a href="mailto:support@invitey.ca">support@invitey.ca</a>.
      </p>

      <p className="muted">
        <Link to="/">&larr; Back to home</Link> &middot;{' '}
        <Link to="/privacy">Privacy Policy</Link>
      </p>
    </div>
  );
}
